/**
 * One bubble. Renders what `thread-view.ts` decided; decides nothing itself.
 *
 * ⛔ EVERY BRANCH HERE IS `switch` OVER A DISCRIMINATED UNION, NOT AN `if` OVER
 * THE ROW. That is deliberate: the moment this file asks `if (message.body)` it
 * has taken a decision the pure module owns, and the two will disagree - which
 * is the `STATUS_RANK` shape, and the reason `bodyRender` returns a tagged
 * result instead of a string.
 */
import { useState } from 'react'
import {
  authorName,
  bodyRender,
  mediaRender,
  quoteRender,
  rowKind,
  systemAttribution,
} from './thread-view.js'
import { renderMessageError } from './message-error.js'
import type { ThreadMessage as ThreadRow } from '../../../src/services/thread.js'

/**
 * ⛔ A SYSTEM LINE BLENDS INTO THE BACKGROUND. Item 9: *"They are context, not
 * content."* No bubble, no border, no author.
 *
 * ⚠️ AND IT IS NOT `role="status"`. These are historical rows arriving in a page
 * of messages, not announcements - a live region would make a screen reader read
 * out four hundred status changes when the thread loads.
 */
function SystemLine({ message }: { message: ThreadRow }): React.JSX.Element {
  const by = systemAttribution(message)
  return (
    <li className="thread-system" data-testid="thread-system">
      {/* ⚠️ The body verbatim. `bodyRender` is about a bubble's content rules -
          templates, media, the unrenderable arm - and a system row has none of
          them. An empty one says so rather than rendering a blank line. */}
      {message.body?.trim() ? message.body : 'This event has no description.'}
      {/*
        ⛔ WHO DID IT. Amir's inspection: these lines named nobody.
        `systemAttribution` owns which actors are named and which are not - a
        customer reopening their own conversation is not attributed, because
        naming them in their own thread reads as if a colleague acted.
      */}
      {by !== null && (
        <span className="thread-system__by" data-testid="thread-system-by">
          {` ${by}`}
        </span>
      )}
    </li>
  )
}

/**
 * ⛔ AN INTERNAL NOTE, YELLOW AND CENTRED. Item 9, and the reason is stated in
 * the plan: *"Distinct at a glance from message bubbles, which is the point - a
 * note is not part of the customer conversation and should never be mistaken
 * for one."*
 *
 * ⚠️ COLOUR IS NOT THE ONLY CARRIER. A note also says the word "Note", because
 * yellow is the whole signal for a person who cannot see it and
 * `ops/check-colors.sh` cannot tell a token apart from a meaning.
 */
function Note({
  message,
  attribution,
  canEdit,
  onEdit,
  onDelete,
  onPin,
}: {
  message: ThreadRow
  attribution: string
  canEdit: boolean
  onEdit?: (noteId: string, body: string) => void | Promise<void>
  onDelete?: (noteId: string) => void | Promise<void>
  onPin?: (noteId: string, pinned: boolean) => void | Promise<void>
}): React.JSX.Element {
  const [draft, setDraft] = useState<string | null>(null)
  const editing = draft !== null

  return (
    <li
      className={`thread-note${message.pinned_at !== null ? ' thread-note--pinned' : ''}`}
      data-testid="thread-note"
    >
      {/* ⛔ THE WORDS ARE NOT CHOSEN HERE. Four cases live in `rowKind`, where
          they can be enumerated against the `sender_type` vocabulary rather than
          read off a nested ternary. */}
      <p className="thread-note__attribution">
        {/**
         * ⛔ WHICH BOOKING, WHEN IT IS A BOOKING NOTE. Amir 2026-09-12:
         * "Booking #10750 · Payment completed". A contact may hold several, so
         * without the tag the reader cannot tell which reservation the note is
         * about - and a contact note deliberately carries none, because the
         * person is already on screen.
         *
         * ⚠️ THE ENTITY LABEL IS NOT AVAILABLE HERE and the tag says "Booking"
         * regardless, which is wrong under a deployment that calls them Rides.
         * Recorded rather than bodged: the thread has never needed `org_settings`
         * and threading it through for one word is a change to this component's
         * inputs. `entity-label-not-available-in-the-thread`.
         */}
        {message.note_booking_reference !== null && (
          <span className="thread-note__booking" data-testid="thread-note-booking">
            Booking #{message.note_booking_reference}
            {' · '}
          </span>
        )}
        {attribution}
        {/* ⚠️ THE EDIT IS DISCLOSED. A note is what colleagues rely on being
            what somebody actually wrote, so a rewrite that looked like an
            original would be the one failure this feature must not introduce. */}
        {message.edited_at !== null && (
          <span className="thread-note__edited" data-testid="thread-note-edited">
            {' '}
            (edited)
          </span>
        )}
        {message.pinned_at !== null && (
          <span className="thread-note__pin-mark" data-testid="thread-note-pinned">
            {' '}
            (pinned)
          </span>
        )}
      </p>

      {editing ? (
        <div className="thread-note__edit">
          <textarea
            className="thread-note__input"
            data-testid="thread-note-input"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            aria-label="Edit note"
          />
          <div className="thread-note__actions">
            <button
              type="button"
              className="thread-note__action"
              data-testid="thread-note-save"
              // An empty edit is a delete wearing another verb, and delete
              // destroys the body - so it is refused rather than silently done.
              disabled={draft.trim() === ''}
              onClick={() => {
                void onEdit?.(message.id, draft.trim())
                setDraft(null)
              }}
            >
              Save
            </button>
            <button
              type="button"
              className="thread-note__action"
              data-testid="thread-note-cancel"
              onClick={() => setDraft(null)}
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <p className="thread-note__body">
            {message.body?.trim() ? message.body : 'This note is empty.'}
          </p>
          <div className="thread-note__actions">
            {/* ⛔ EDIT IS THE AUTHOR'S ALONE. The attribution does not change
                when somebody else rewrites it, so the reader would have no way
                to know. The service enforces this; the UI simply does not offer
                what would be refused. */}
            {canEdit && onEdit !== undefined && (
              <button
                type="button"
                className="thread-note__action"
                data-testid="thread-note-edit"
                onClick={() => setDraft(message.body ?? '')}
              >
                Edit
              </button>
            )}
            {onPin !== undefined && (
              <button
                type="button"
                className="thread-note__action"
                data-testid="thread-note-pin"
                onClick={() => void onPin(message.id, message.pinned_at === null)}
              >
                {message.pinned_at === null ? 'Pin' : 'Unpin'}
              </button>
            )}
            {/* ⚠️ ANY COLLEAGUE MAY DELETE, unlike edit: the motivating case is
                a customer's medical detail in the wrong thread and it must not
                wait for the author to return. */}
            {onDelete !== undefined && (
              <button
                type="button"
                className="thread-note__action thread-note__action--danger"
                data-testid="thread-note-delete"
                onClick={() => void onDelete(message.id)}
              >
                Delete
              </button>
            )}
          </div>
        </>
      )}
    </li>
  )
}

export type ThreadMessageProps = {
  message: ThreadRow
  /**
   * ⛔ THE VISUALLY-PREVIOUS BUBBLE, WHICH IS `rows[i + 1]` AND NOT `rows[i-1]`.
   *
   * The thread page arrives NEWEST FIRST, matching the conversation list and the
   * keyset cursor. So the message rendered ABOVE this one - the one it might be
   * continuing a run from - is the NEXT element in the array.
   *
   * ⚠️ This reads as a bug to anyone who has not held the ordering in mind, and
   * it has been written wrong once already in a scratch version. The caller
   * passes it explicitly rather than this component indexing, so the ordering
   * lives in one place.
   */
  previous: ThreadRow | undefined
  startsRun: boolean
  /**
   * ⛔ THE MESSAGE A SEARCH RESULT ARRIVED AT. `Build_Order:490` requires it to
   * be *highlighted*, and the class is only half of that - see `thread.css`,
   * where the rule is a border and a background rather than colour alone, for
   * the reason the yellow note carries the word "Note".
   */
  highlighted?: boolean
  /**
   * The signed-in user, so the row can offer Edit only to a note's author.
   * `null` means unknown, and unknown offers nothing - the service would refuse
   * anyway, and a control that always fails is worse than no control.
   */
  currentUserId?: string | null
  onEditNote?: (noteId: string, body: string) => void | Promise<void>
  onDeleteNote?: (noteId: string) => void | Promise<void>
  onPinNote?: (noteId: string, pinned: boolean) => void | Promise<void>
}

export function ThreadMessage({
  message,
  startsRun,
  highlighted = false,
  currentUserId = null,
  onEditNote,
  onDeleteNote,
  onPinNote,
}: ThreadMessageProps): React.JSX.Element {
  /**
   * ⛔ THE ROW KIND IS ASKED FIRST, BEFORE ANY BUBBLE MACHINERY. Item 9: notes
   * and system lines render **INLINE AND CENTRED**, not as bubbles - *"they are
   * part of the conversation's narrative, and moving them elsewhere means
   * reading two places to follow one story."*
   *
   * ⚠️ THEY RETURN EARLY RATHER THAN SHARING THE BUBBLE WITH A MODIFIER CLASS.
   * A note has no avatar, no run grouping, no reactions, no delivery error and
   * no quote - so a shared render would be five branches each spelling out that
   * this row is not that kind of thing, which is the shape that rots.
   */
  const kind = rowKind(message)
  if (kind.kind === 'system') return <SystemLine message={message} />
  if (kind.kind === 'deletedNote') {
    /**
     * ⛔ A TOMBSTONE, NOT A GAP. Migration `0019` destroys the body and keeps
     * the row precisely so the trail survives; rendering nothing would throw
     * away the half of the decision that cost the design its complexity.
     *
     * ⚠️ AND IT SAYS "deleted" RATHER THAN SHOWING WHO. The actor is on the row
     * and deliberately not here: naming a colleague beside a removal invites
     * reading it as an accusation, and the audit answer belongs where somebody
     * is auditing rather than in the middle of a conversation.
     */
    return (
      <li className="thread-note thread-note--deleted" data-testid="thread-note-deleted">
        Note deleted
      </li>
    )
  }
  if (kind.kind === 'note') {
    return (
      <Note
        message={message}
        attribution={kind.attribution}
        canEdit={message.sender_user_id !== null && message.sender_user_id === currentUserId}
        onEdit={onEditNote}
        onDelete={onDeleteNote}
        onPin={onPinNote}
      />
    )
  }

  const outbound = message.direction === 'outbound'
  const author = startsRun ? authorName(message) : null
  const body = bodyRender(message)
  /**
   * ⚠️ FROM `error_code` ALONE, NEVER FROM `status`. A message can be `failed`
   * with no code, and `messages.error_code` can be set on a row whose status
   * moved on afterwards. **The code is what carries a REASON**, and rule 4 is
   * about the reason reaching the agent - not about the fact of failure, which
   * the status already shows.
   */
  const failure = renderMessageError(message.error_code)
  const quote = quoteRender(message)

  return (
    <li
      /**
       * ⚠️ FULL CLASS NAMES IN THE CONDITIONAL, NOT A SUFFIX CONCATENATED ONTO
       * A PREFIX. `thread-msg--${outbound ? 'out' : 'in'}` produces the same
       * markup and is worse in two ways: a reader grepping for
       * `thread-msg--out` finds nothing, and `ops/check-css-classes.sh` extracts
       * the quoted fragments as if they were whole class names and reports
       * `.in` and `.out` as unstyled. **The scanner is right to be confused -
       * so is a human.**
       */
      className={`thread-msg ${outbound ? 'thread-msg--out' : 'thread-msg--in'}${startsRun ? '' : ' thread-msg--cont'}${highlighted ? ' thread-msg--found' : ''}`}
      data-testid="thread-message"
      // ⛔ THE SCROLL TARGET, found by ATTRIBUTE rather than by index: the
      // arrived-at message can be anywhere in the page.
      data-message-id={message.id}
    >
      {/*
        ⛔ THE AVATAR IS ABSENT, NOT HIDDEN, ON A CONTINUATION. `display: none`
        leaves it in the accessibility tree and in the document, so a jsdom test
        asserting "the avatar is not shown" would pass on a bubble that still
        announces the sender to a screen reader on every line of a run.
      */}
      {startsRun && !outbound && (
        <span className="thread-msg__avatar" aria-hidden="true" data-testid="thread-avatar" />
      )}

      <div className="thread-msg__bubble">
        {author !== null && <p className="thread-msg__author">{author}</p>}

        {quote !== null && (
          <blockquote
            className={`thread-msg__quote${quote.missing ? ' thread-msg__quote--missing' : ''}`}
            data-testid="thread-quote"
          >
            {quote.text}
          </blockquote>
        )}

        {message.media?.items.map((item, i) => {
          const m = mediaRender(item, message.direction)
          return (
            <div
              key={`${message.id}-media-${i}`}
              className={`thread-msg__media${m.failed ? ' thread-msg__media--failed' : ''}${m.pending ? ' thread-msg__media--pending' : ''}`}
              data-testid="thread-media"
            >
              {m.showsContent ? (
                /*
                 * ⚠️ `alt` IS THE SAME LABEL THE FAILURE ARMS SHOW. A photo that
                 * fails to load in the browser then degrades to the same words
                 * as a photo that failed to download, rather than to a blank.
                 */
                <img
                  className="thread-msg__media-img"
                  src={`/api/media/${message.id}/${i}`}
                  alt={m.label}
                />
              ) : (
                <p className="thread-msg__media-note">{m.label}</p>
              )}
            </div>
          )
        })}

        {/*
          ⛔ NO BRANCH RENDERS NOTHING. `bodyRender` guarantees a tag for every
          row, and `media-only` is the only one that emits no text - because the
          attachment above IS the content, which is different from empty.
        */}
        {body.kind === 'text' && <p className="thread-msg__body">{body.text}</p>}
        {body.kind === 'template' && (
          <p className="thread-msg__body thread-msg__body--template" data-testid="thread-template">
            Template: {body.templateName}
          </p>
        )}
        {body.kind === 'unrenderable' && (
          <p className="thread-msg__body thread-msg__body--broken" data-testid="thread-broken">
            {body.reason}
          </p>
        )}

        {/*
          ⛔ RULE 4: BOTH LAYERS, ALWAYS, AND THE DIAGNOSTIC IS NOT BEHIND A
          DISCLOSURE. INBOX scope item 12.

          The build plan requires "EVERY ERROR CARRIES THE META ERROR CODE AND
          ITS MEANING, not just a friendly message", so every failure renders an
          agent line AND a diagnostic line.

          ⚠️ ALWAYS VISIBLE IS AN INHERITED CONSTRAINT RATHER THAN A UI
          PREFERENCE: nothing in this system alerts on an `error` log line, so
          this element is the only place a refusal surfaces at all. A disclosure
          hides the one copy. It can become one later, when something watches.

          ⛔ THE WORDS ARE NOT DECIDED HERE. `renderMessageError` owns which case
          applies and what each line says, and is asserted against the vocabulary
          read out of the live CHECK constraint in `tests/message-error.test.ts`.
          A component deciding this would be nine branches nobody could enumerate.
        */}
        {failure !== null && (
          <div className="thread-msg__error" role="status" data-testid="thread-error-line">
            <p className="thread-msg__error-agent">{failure.agent}</p>
            <p className="thread-msg__error-diagnostic">{failure.diagnostic}</p>
            {failure.retry.kind !== 'none' && (
              <p className="thread-msg__error-action" data-testid="thread-error-action">
                {/*
                  ⛔ A TEMPLATE, NOT A RETRY, WHEN THE WINDOW IS CLOSED.
                  Resending the same free-form message fails identically, so
                  "retry" would be an instruction that cannot work.

                  ⚠️ NEITHER IS A BUTTON YET. The composer is item 13 and there
                  is nothing to retry WITH; a button that did nothing would be
                  worse than a sentence, because it claims an action exists.
                */}
                {failure.retry.kind === 'retry'
                  ? 'This can be retried once the composer is built.'
                  : 'An approved template can still be sent.'}
              </p>
            )}
          </div>
        )}

        {message.reactions.length > 0 && (
          <ul className="thread-msg__reactions" data-testid="thread-reactions">
            {message.reactions.map((r) => (
              <li
                className="thread-msg__reaction"
                key={`${message.id}-${r.senderType}-${r.senderUserId ?? 'customer'}`}
              >
                {r.emoji}
              </li>
            ))}
          </ul>
        )}
      </div>
    </li>
  )
}
